import React, {useEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {Button, Card, Col, DatePicker, Form, Row, Spin} from "antd";

import {messageType, showMessages} from "../../helpers/message";
import {createInvoice, createInvoiceAllAccounts, sendMassNotifications} from '../../helpers/invoice';
import confirmAction from "../../helpers/confirmAction";
import {formatTimestamp} from '../../helpers/misc';
import {getActiveAccount} from "../../lib/activeMember";
import {dateFormat} from "../../lib/time";

import {grey} from "../../assets/colors";
import {formInCardLayout, formItemFullWidth} from "../../assets/styles";

const {Meta} = Card;

const InvoiceGenerationCard = ({singleAccount}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Cleanup effect when component unmounts
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const handleGenerateFinish = async ({end_date}) => {
        const endOfEndDate = end_date.clone().endOf('day');
        const abortSignal = abortSignalRef.current;

        try {
            setLoading(true);

            if (singleAccount === true) {
                const accountID = getActiveAccount().id;
                await createInvoice({
                    ended_at: formatTimestamp(endOfEndDate),
                    account_id: accountID,
                }, abortSignal);
            } else {
                await createInvoiceAllAccounts({
                    ended_at: formatTimestamp(endOfEndDate),
                }, abortSignal);
            }

            form.resetFields();
            showMessages('Invoice generation has started in the background.', messageType.info);
            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    };

    const handleNotifyFinish = async () => {
        const abortSignal = abortSignalRef.current;

        try {
            setLoading(true);

            await sendMassNotifications(abortSignal);

            showMessages('Sending invoice notifications has started in the background.', messageType.info);
        } catch (error) {
            showMessages(error.message, messageType.error);
        }

        setLoading(false);
    };

    return (
        <Spin spinning={loading}>
        <Row gutter={16}>
            <Col span={12}>
            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    name="invoice-generate"
                    {...formInCardLayout}
                    onFinish={handleGenerateFinish}
                >
                    <Form.Item>
                        <Meta
                            description={
                                <span style={{color: grey}}>
                                    This will generate invoice for all accounts.<br/><br/>
                                    The start date will be determined by the end date of the previous invoice. If there is no invoice on an account, the start date is determined by the account open date.</span>
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="end_date"
                        label="End Date"
                        style={{marginTop: '2rem'}}
                        rules={[{required: true, message: 'Please select an invoice end date'}]}
                    >
                        <DatePicker
                            placeholder="DD MM YYYY"
                            format={dateFormat}
                            style={{...formItemFullWidth}}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Row justify="end">
                            <Col>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                >
                                    Generate
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Card>
            </Col>
            <Col span={12}>
                <Card title="Send Invoice Emails">
                    <Button
                        type="primary"
                        htmlType="submit"
                        onClick={() => confirmAction(
                            'Are you sure you want to send invoice notification emails?',
                            'This will send an email to all accounts with an invoice due at the end of this month with a balance greater than zero.',
                            handleNotifyFinish,
                            undefined,
                            undefined,
                            'Send Emails',
                            'Cancel'
                        )}
                    >
                        Send Emails
                    </Button>
                </Card>
            </Col>
        </Row>
        </Spin>
    );
};
export default InvoiceGenerationCard;

InvoiceGenerationCard.propTypes = {
    singleAccount: PropTypes.bool.isRequired,
};
